import type { BankTransactionDto } from "Infrastructure/Api/Api";
import { defaultPalette } from "Infrastructure/Theme/palettes/default";
import { formatCurrency } from "Infrastructure/Utilities/currencyFormat";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";

const updatePaymentModalSchema = z.object({
	orderId: z.string().optional(),
	isRenovation: z.boolean().optional(),
});

export type UpdatePaymentModalFormData = z.infer<
	typeof updatePaymentModalSchema
>;

type UpdatePaymentModalProps = {
	payment: BankTransactionDto | null;
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (
		data: UpdatePaymentModalFormData,
		payment: BankTransactionDto,
	) => void;
};

/**
 * Updates a bank transaction (payment) with order id or renovation status.
 */
export function UpdatePaymentModal({
	payment,
	isOpen,
	onClose,
	onSubmit,
}: UpdatePaymentModalProps) {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";
	const form = useForm<UpdatePaymentModalFormData>({
		resolver: zodResolver(updatePaymentModalSchema),
		defaultValues: {
			orderId: undefined,
			isRenovation: false,
		},
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isDirty },
	} = form;

	const handleClose = () => {
		reset();
		onClose();
	};

	const handleFormSubmit = (data: UpdatePaymentModalFormData) => {
		onSubmit(data, payment!);
		reset();
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle variant="h3">Assign Order</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<DialogContentText>
					Manually assign this payment to an order. Enter the Order ID.
				</DialogContentText>
				<Box
					display="flex"
					flexDirection="column"
					gap={2}
					p={2}
					bgcolor={
						isDarkMode ? defaultPalette.grey[600] : defaultPalette.grey[100]
					}
				>
					<Typography variant="body1" fontWeight="bold">
						Payment Details
					</Typography>
					{payment && (
						<Stack spacing={1}>
							<Typography>
								<span>Amount: </span>
								{formatCurrency(
									payment.currency,
									payment.currency,
									payment.amount,
								)}
							</Typography>
							<Typography>
								<span>Reference: </span>
								{payment.reference}
							</Typography>
							<Typography>
								<span>Note: </span>
								{payment.note}
							</Typography>
							<Typography>
								<span>Bank Account: </span>
								{payment.bankDetails.bankAccount}
							</Typography>
							<Typography>
								<span>IBAN: </span>
								{payment.bankDetails.iban}
							</Typography>
						</Stack>
					)}
				</Box>

				<Input
					id="orderId"
					name="orderId"
					control={control}
					errors={errors}
					placeholder="Enter Order ID"
					type="number"
					variant="outlined"
					fullWidth
				/>
				<Stack direction="row" alignItems="center">
					<Checkbox
						id="isRenovation"
						name="isRenovation"
						control={control}
						errors={errors}
					/>
					<Typography variant="subtitle1">Mark order as Renovation</Typography>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit(handleFormSubmit)}
					disabled={!isDirty}
				>
					Update
				</Button>
			</DialogActions>
		</Dialog>
	);
}
